/* eslint-disable react-refresh/only-export-components --
 * `getAttachments` travels with the panel that renders its result; the
 * per-email Review screen and the details sheet both read it from here. */
import { useId, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';
import { formatSourceForReading } from '@/lib/sourceReading';
import type { Email } from '@/types';

export interface AttachmentInfo {
  filename: string;
  mime_type?: string;
}

export interface EmailSourcePanelProps {
  email: Email;
  attachments: AttachmentInfo[];
  /** The Sources card already shows the subject above the panel. Default false. */
  hideSubject?: boolean;
  className?: string;
}

function attachmentIcon(mime?: string) {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('image/'))
    return (
      <ImageIcon
        size={16}
        className="text-muted-foreground"
      />
    );
  if (m === 'application/pdf')
    return (
      <FileText
        size={16}
        className="text-muted-foreground"
      />
    );
  return (
    <FileIcon
      size={16}
      className="text-muted-foreground"
    />
  );
}

/**
 * An email read in place: subject, body as prose, the folded newsletter
 * footer, attachment chips and the Open in Gmail link. Extracted from the
 * per-email Review screen's "View Source Reference" section.
 */
export function EmailSourcePanel({
  email,
  attachments,
  hideSubject = false,
  className = '',
}: EmailSourcePanelProps) {
  const [footerExpanded, setFooterExpanded] = useState(false);
  const footerId = useId();
  const content = email.processed_content;
  const source = useMemo(
    () => (content ? formatSourceForReading(content) : null),
    [content],
  );

  return (
    <div
      data-testid="email-source-panel"
      className={[
        'rounded-xl border border-border p-5 flex flex-col gap-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Title */}
      {!hideSubject && (
        <p className="text-base font-semibold text-foreground m-0">
          {email.subject}
        </p>
      )}

      {/* Body — `formatSourceForReading` normalizes CRLF, strips links
          (YAR-468: the cleaner keeps every anchor as `label (href)`, and
          the links live on the extracted items and in Gmail), drops
          orphaned button labels, squeezes blank-line walls to one
          paragraph break and joins hard-wrapped lines. Rendered as a
          text node — never as HTML. */}
      {source && source.body !== '' && (
        <div className="text-sm text-foreground whitespace-pre-line leading-relaxed break-words">
          {source.body}
        </div>
      )}

      {/* Newsletter footer (unsubscribe / "you are receiving this")
          folded away, not deleted. */}
      {source?.footer && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            aria-expanded={footerExpanded}
            aria-controls={footerId}
            onClick={() => setFooterExpanded((open) => !open)}
            className="inline-flex items-center gap-1 self-start bg-transparent border-none p-0 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {footerExpanded ? 'Hide footer' : 'Show footer'}
            {footerExpanded ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )}
          </button>
          {footerExpanded && (
            <div
              id={footerId}
              className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed break-words"
            >
              {source.footer}
            </div>
          )}
        </div>
      )}

      {/* Attachments — chips show filename + a file-type icon. The
          binary isn't downloaded; we surface the metadata so users
          can confirm the email actually had the attachment they
          expected (LLM can't see it, hence the visibility here). */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 py-2">
          {attachments.map((att, idx) => (
            <span
              key={`${att.filename}-${idx}`}
              className="inline-flex items-center gap-1.5 px-1.5 py-1 bg-background rounded-md text-sm font-semibold text-muted-foreground border border-border"
            >
              {attachmentIcon(att.mime_type)}
              {att.filename}
            </span>
          ))}
        </div>
      )}

      {/* Open in Gmail */}
      {email.provider_message_id && (
        <a
          // `#all/`, not `#inbox/`. Yaruta labels the mail it captures but
          // never archives it (`addLabelIds` only, no `removeLabelIds`), so
          // the message is still in Gmail — but not necessarily in the
          // Inbox view. Gmail files promotional mail under the Promotions
          // tab, and the user may have archived it themselves; in both
          // cases an `#inbox/` link opens an empty view and the mail reads
          // as missing. `#all/` resolves the id against All Mail, which
          // contains every non-deleted message regardless of tab or label.
          href={`https://mail.google.com/mail/u/0/#all/${email.provider_message_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 w-full rounded-sm border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-surface-secondary transition-colors duration-150 no-underline"
        >
          Open in Gmail
          <ExternalLink size={16} />
        </a>
      )}
    </div>
  );
}

export function getAttachments(email: Email): AttachmentInfo[] {
  if (!email.preprocessing_metadata) return [];
  try {
    const meta =
      typeof email.preprocessing_metadata === 'string'
        ? JSON.parse(email.preprocessing_metadata)
        : email.preprocessing_metadata;
    if (Array.isArray(meta?.attachments)) {
      return meta.attachments.map(
        (a: { filename?: string; name?: string; mime_type?: string }) => ({
          filename: a.filename || a.name || 'Attachment',
          mime_type: a.mime_type,
        }),
      );
    }
  } catch {
    // ignore parse errors
  }
  return [];
}
